import { useId, type HTMLAttributes } from 'react';
import { cn } from '../../lib/cn';

/**
 * MINIGRÁFICO — uma forma sem eixo ao lado de um número (G5 e Curva de Evasão, 07/10/2026).
 *
 * Serve à forma do dia (24 h, card de campanha e herói do Dashboard) e às últimas 28 turmas
 * da evasão. As duas frentes criaram a peça em paralelo; esta é a versão unificada.
 *
 * ### `null` não é zero
 *
 * Cada ponto é um número medido ou `null`. Os `null` do FIM da série (as horas que ainda não
 * chegaram) simplesmente não são desenhados — a linha para em "agora". Um `null` no MEIO da
 * série (hora sem medição) vira trecho hachurado com a linha interrompida, nunca uma queda a
 * zero: desenhar zero ali afirmaria "ninguém entrou". Série sem nenhum ponto medido diz
 * "sem medição" ao leitor de tela. Um `0` medido desenha a linha no chão.
 *
 * A escala é só desenho (o maior valor, ou `max`, ocupa a altura); nenhum número é calculado.
 */
export interface SparklineProps extends Omit<HTMLAttributes<HTMLElement>, 'children'> {
  /** Um ponto por balde (hora, dia). `null` = sem medição. */
  values: ReadonlyArray<number | null>;
  /** Quantos baldes o eixo tem (padrão: `values.length`). 24 para "o dia", mesmo às 9h. */
  slots?: number;
  /** Descrição para leitor de tela ("Entradas de hoje, hora a hora"). */
  label: string;
  /** Teto do eixo. Padrão: o maior valor medido (ou 1). Fixe em 1 para frações. */
  max?: number;
  /** `brand` = sobre fundo violeta (herói); `default` = sobre card. */
  tone?: 'default' | 'brand';
  /** Altura em classe de token (padrão `h-10`). */
  heightClassName?: string;
}

const LARGURA = 240;
const ALTURA = 64;

type Trecho = { pontos: Array<[number, number]> };

export function Sparkline({
  values,
  slots,
  label,
  max: teto,
  tone = 'default',
  heightClassName = 'h-10',
  className,
  ...props
}: SparklineProps) {
  const padrao = `hachura-${useId().replace(/:/g, '')}`;
  const total = Math.max(2, slots ?? values.length);
  const passo = LARGURA / (total - 1);
  // o último ponto medido: depois dele, o futuro — não se desenha nada
  let ultimo = -1;
  values.forEach((v, i) => {
    if (v !== null && Number.isFinite(v)) ultimo = i;
  });
  const medidos = values.filter((v): v is number => v !== null && Number.isFinite(v));
  const max = teto !== undefined && teto > 0 ? teto : Math.max(1, ...medidos);
  const y = (v: number) => ALTURA - 4 - (Math.min(v, max) / max) * (ALTURA - 8);

  const trechos: Trecho[] = [];
  const buracos: Array<{ de: number; ate: number }> = [];
  let atual: Trecho | null = null;
  for (let i = 0; i <= ultimo; i += 1) {
    const v = values[i];
    if (v === null || v === undefined || !Number.isFinite(v)) {
      if (atual) {
        trechos.push(atual);
        atual = null;
      }
      const anterior = buracos.at(-1);
      if (anterior && anterior.ate === i - 1) anterior.ate = i;
      else buracos.push({ de: i, ate: i });
      continue;
    }
    atual ??= { pontos: [] };
    atual.pontos.push([i * passo, y(v)]);
  }
  if (atual) trechos.push(atual);

  const linha = (t: Trecho) =>
    t.pontos.length === 1
      ? `M${t.pontos[0]![0].toFixed(1)},${t.pontos[0]![1].toFixed(1)} h0.1`
      : `M${t.pontos.map(([x, yy]) => `${x.toFixed(1)},${yy.toFixed(1)}`).join(' L')}`;
  const area = (t: Trecho) => {
    const primeiro = t.pontos[0]![0];
    const fim = t.pontos.at(-1)![0];
    return `${linha(t)} L${fim.toFixed(1)},${ALTURA} L${primeiro.toFixed(1)},${ALTURA} Z`;
  };

  const brand = tone === 'brand';
  const semNenhuma = medidos.length === 0;
  const semMedicao = buracos.length > 0;
  const rotulo = semNenhuma
    ? `${label}: sem medição`
    : semMedicao
      ? `${label} (com trecho sem medição)`
      : label;

  return (
    <figure role="img" className={cn('m-0 w-full', className)} aria-label={rotulo} {...props}>
      <svg
        viewBox={`0 0 ${LARGURA} ${ALTURA}`}
        className={cn('w-full overflow-visible', heightClassName)}
        preserveAspectRatio="none"
        aria-hidden="true"
      >
        {semMedicao ? (
          <defs>
            <pattern
              id={padrao}
              width={6}
              height={6}
              patternUnits="userSpaceOnUse"
              patternTransform="rotate(45)"
            >
              <line x1={0} y1={0} x2={0} y2={6} className="stroke-border-strong" strokeWidth={2} />
            </pattern>
          </defs>
        ) : null}
        {buracos.map((b) => (
          <rect
            key={b.de}
            data-sem-medicao=""
            x={Math.max(0, (b.de - 0.5) * passo)}
            y={0}
            width={Math.max(passo, (b.ate - b.de + 1) * passo)}
            height={ALTURA}
            fill={`url(#${padrao})`}
            opacity={0.7}
          />
        ))}
        {trechos.map((t) => (
          <g key={t.pontos[0]![0]}>
            {t.pontos.length > 1 ? (
              <path
                d={area(t)}
                className={brand ? 'fill-primary-foreground/15' : 'fill-primary/10'}
              />
            ) : null}
            <path
              d={linha(t)}
              className={cn('fill-none', brand ? 'stroke-primary-foreground' : 'stroke-primary')}
              strokeWidth={2}
              strokeLinecap="round"
              strokeLinejoin="round"
              vectorEffect="non-scaling-stroke"
            />
          </g>
        ))}
      </svg>
    </figure>
  );
}
